import React, { useState } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { createAnnotation } from '../../annotations/annotationStore';
import SensePicker from '../../yasna/SensePicker';
import SenseCount from '../../annotations/SenseCount';
import SenseCard from '../../learner/SenseCard';
import './AnnotationForm.css';

/**
 * AnnotationForm — link the selected subtitle span to one yasna word sense.
 * (Words typed without a subtitle go through MomentAnnotator instead.)
 * Dictionary entries live in yasna (see src/yasna/loadYasna.js); hoja only
 * records which sense this span uses.
 *
 * Props:
 *   surfaceText — the selected text from the subtitle (e.g. "me rendu compte")
 *   wordIndices — array of word positions in the subtitle
 *   subtitle — the subtitle object { id, videoId, startTime, endTime, ... }
 *   language — the video's target language ('fr' | 'de')
 *   onSaved(annotation) — called after annotation is saved
 *   onCancel() — called when user cancels
 */
function AnnotationForm({ surfaceText, wordIndices, subtitle, language, onSaved, onCancel }) {
  const [selectedSense, setSelectedSense] = useState(null);
  const [annotatorNote, setAnnotatorNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSaveAnnotation = async () => {
    if (!selectedSense || !subtitle) return;
    setError('');
    setSaving(true);

    try {
      const annotationDoc = {
        videoId: subtitle.videoId,
        subtitleId: subtitle.id,
        startTime: subtitle.startTime,
        endTime: subtitle.endTime,
        surfaceText,
        senseId: selectedSense.word_sense_id,
        language: selectedSense.language,
        wordIndices,
        annotatorNote: annotatorNote.trim() || null,
        createdAt: serverTimestamp(),
        createdBy: 'annotator', // POC: no user ID
      };

      const annotation = await createAnnotation(annotationDoc);
      onSaved && onSaved(annotation);
    } catch (e) {
      console.error('Error saving annotation:', e);
      setError('Failed to save annotation. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div className="annotation-form">
      <div className="annotation-form-header">
        <h4 className="annotation-form-title">Annotate</h4>
        <button className="annotation-form-close" onClick={onCancel} aria-label="Cancel annotation">
          ×
        </button>
      </div>

      <div className="annotation-form-surface">
        <span className="annotation-form-surface-label">Surface text:</span>
        <span className="annotation-form-surface-text">"{surfaceText}"</span>
      </div>

      {/* Pick the yasna sense this span uses */}
      <div className="annotation-form-search">
        <label className="annotation-form-label">Link to dictionary sense:</label>
        <SensePicker
          initialTerm={surfaceText}
          language={language}
          selectedId={selectedSense && selectedSense.word_sense_id}
          onSelect={setSelectedSense}
          renderExtra={(s) => <SenseCount senseId={s.word_sense_id} />}
        />
      </div>

      {/* Preview the entry as learners get it: every card, the ones they see under More marked */}
      {selectedSense && (
        <div className="annotation-form-entry">
          <SenseCard key={selectedSense.word_sense_id} senseId={selectedSense.word_sense_id} teacher />
          <label className="annotation-form-label" htmlFor="annotation-note">
            Note for this video (optional):
          </label>
          <textarea
            id="annotation-note"
            className="annotation-form-note"
            value={annotatorNote}
            onChange={(e) => setAnnotatorNote(e.target.value)}
            placeholder="e.g. here it's sarcastic"
            rows={2}
          />
        </div>
      )}

      {error && <p className="annotation-form-error">{error}</p>}

      {/* Save annotation button */}
      {selectedSense && (
        <div className="annotation-form-actions">
          <button
            className="annotation-form-save-btn"
            onClick={handleSaveAnnotation}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save Annotation'}
          </button>
        </div>
      )}
    </div>
  );
}

export default AnnotationForm;
